import type { QuestionAgentRate, QuestionEvaluationRow } from '@compass/client/types/analytics';

import type { NormalizedExport } from './normalizeChartRows';

// AQ-608 question deep-dive helpers (pure — 100% jest-covered).

type QuestionAnswer = 'No' | 'Partial';

// deriveAnswer maps a finding's raw_score to the "Answer" shown in the
// missed-evaluations table. Only missed rows (raw_score < 1) reach this table:
// an exact 0 is a full "No"; anything between 0 and 1 is partial credit.
export const deriveAnswer = (rawScore: number): QuestionAnswer =>
  rawScore <= 0 ? 'No' : 'Partial';

// formatPercent1 renders a 0..1 rate as a one-decimal percentage ("0.387" → "38.7%").
export const formatPercent1 = (rate: number): string => `${(rate * 100).toFixed(1)}%`;

// formatPotentialPp renders a percentage-point delta with a leading sign ("+1.2 pp").
export const formatPotentialPp = (pp: number): string => {
  const rounded = Math.round(pp * 10) / 10;
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(1)} pp`;
};

// sortAgentRatesByPotential sorts agents by improvement potential (pp) desc,
// tiebreak by missed count desc — matching the deep-dive table's default sort
// (QuestionAgentBreakdownTable), so the exported file's row order and its
// "Potential (pp)" column agree with what the user saw on screen.
export const sortAgentRatesByPotential = (rows: QuestionAgentRate[]): QuestionAgentRate[] =>
  [...rows].sort((a, b) => {
    const ap = a.potential_pp ?? 0;
    const bp = b.potential_pp ?? 0;
    if (ap !== bp) return bp - ap;
    return (b.missed_count ?? 0) - (a.missed_count ?? 0);
  });

const pct1 = (rate: number): number => Number((rate * 100).toFixed(1));

// buildQuestionAgentsExport turns the full agent set into a NormalizedExport for
// the shared analytics download menu (CSV / Excel / PDF via useDownloadChart),
// sorted by potential (pp) desc to match the on-screen table's default order.
// Rates/scores are numeric percentages so Excel keeps them sortable.
export const buildQuestionAgentsExport = (rows: QuestionAgentRate[]): NormalizedExport => ({
  filenameStem: 'question_agent_breakdown',
  columns: ['Agent', 'Miss rate (%)', 'Missed', 'Total', 'Avg score (%)', 'Potential (pp)'],
  rows: sortAgentRatesByPotential(rows).map(r => ({
    Agent: r.agent_name ?? r.agent_id ?? '',
    'Miss rate (%)': pct1(r.miss_rate ?? 0),
    Missed: r.missed_count ?? 0,
    Total: r.total_count ?? 0,
    'Avg score (%)': pct1(r.avg_score ?? 0),
    'Potential (pp)': Number((r.potential_pp ?? 0).toFixed(1)),
  })),
});

// slugifyForFilename turns a question label into a safe filename stem fragment
// ("HIPAA — Patient" → "hipaa_patient"); empty/odd labels fall back to "question".
const slugifyForFilename = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'question';

// buildQuestionEvaluationsExport turns the FULL set of missed evaluations (the UI
// shows only the 5 most recent) into a NormalizedExport for the shared download
// menu. Rows arrive newest-first from the backend; that order is preserved.
// call_start_time is the raw UTC ISO string (header says so) to stay unambiguous
// in a spreadsheet; score is a numeric percentage so Excel keeps it sortable.
export const buildQuestionEvaluationsExport = (
  rows: QuestionEvaluationRow[],
  label?: string,
): NormalizedExport => ({
  filenameStem: `${label ? slugifyForFilename(label) : 'question'}_missed_evaluations`,
  columns: ['Evaluation ID', 'Call ID', 'Agent', 'Call Start Time (UTC)', 'Score (%)', 'Answer'],
  rows: rows.map(r => ({
    'Evaluation ID': r.evaluation_id ?? '',
    'Call ID': r.call_id ?? '',
    Agent: r.agent_name ?? r.agent_id ?? '',
    'Call Start Time (UTC)': r.call_start_time ?? '',
    'Score (%)': pct1(r.aggregate_score ?? 0),
    Answer: deriveAnswer(r.raw_score ?? 0),
  })),
});
